"use client";

import Stack, { type StackProps } from "@mui/material/Stack";
import { SystemStyleObject, Theme } from "@mui/system";
import type { ReactNode } from "react";
import {
  FormProvider,
  type FieldValues,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormReturn,
} from "react-hook-form";

export type FormProps<T extends FieldValues> = {
  formId?: string;
  methods: UseFormReturn<T>;
  onSubmit: SubmitHandler<T>;
  onError?: SubmitErrorHandler<T>;
  children: ReactNode;
  spacing?: StackProps["spacing"];
  sx?: StackProps["sx"];
};

export const Form = <T extends FieldValues>({
  formId,
  methods,
  onSubmit,
  onError,
  children,
  spacing = 4,
  sx,
}: FormProps<T>) => {
  return (
    <FormProvider {...methods}>
      <Stack
        id={formId}
        component="form"
        noValidate
        spacing={spacing}
        sx={(theme) =>
          ({
            flex: 1,
            paddingY: theme.spacing(1),
            ...(typeof sx === "function" ? sx(theme) : sx ?? {}),
          }) as SystemStyleObject<Theme>
        }
        onSubmit={methods.handleSubmit(onSubmit, onError)}
      >
        {children}
      </Stack>
    </FormProvider>
  );
};
